import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label } from "../roads/_roadsCommon";

/** Ch 29 — BIM Coordination + Multi-Discipline Clash + IFC 4 Export. */
export default function BIMCoordinationPanel({ projectId }) {
  const [federated, setFederated] = useState(null);
  const [clashes, setClashes] = useState(null);
  const [ifcMeta, setIfcMeta] = useState(null);
  const [resolution, setResolution] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [f, c] = await Promise.all([
        api.get(`/projects/${projectId}/bim/federated`),
        api.get(`/projects/${projectId}/bim/clashes`),
      ]);
      setFederated(f.data);
      setClashes(c.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const exportIFC = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/bim/ifc`);
      setIfcMeta({ size: r.data.size_bytes, lines: r.data.lines, ifc: r.data.ifc });
      // Auto-download
      const blob = new Blob([r.data.ifc], { type: "application/x-step" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${projectId}.ifc`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    setLoading(false);
  };

  return (
    <div data-testid="bim-coordination" style={{ display: "grid",
            gridTemplateColumns: "1fr 1fr", gap: 12, padding: 16,
            height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Federated model summary */}
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr auto", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 14 }}>
          <div style={label}>29.1 / 29.4 — Federated Model Summary</div>
          {federated && (
            <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.7,
                              display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <DiscBlock title="Architecture" data={federated.architecture} />
                <DiscBlock title="Structural" data={federated.structural} />
              </div>
              <div>
                <DiscBlock title="MEP" data={federated.mep} />
                <DiscBlock title="Site" data={federated.site} />
              </div>
            </div>
          )}
        </div>

        {/* IFC export */}
        <div style={{ ...card, padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={label}>29.3 — IFC 4 Export (BIM Level 2)</div>
            <button data-testid="bim-export-ifc" style={btn}
                onClick={exportIFC} disabled={loading}>
              {loading ? "Exporting…" : "↓ Generate & download .ifc"}
            </button>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--pf-muted)" }}>
            Maps walls → IfcWall · columns → IfcColumn · beams → IfcBeam ·
            slabs → IfcSlab · footings → IfcFooting · ducts → IfcDuctSegment ·
            pipes → IfcPipeSegment · cable trays → IfcCableCarrierSegment ·
            rooms → IfcSpace. STEP-21 file opens in Revit / Tekla / Navisworks /
            BIMcollab.
          </div>
          {ifcMeta && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#22c55e" }}>
              ✓ Generated {ifcMeta.lines} lines · {(ifcMeta.size / 1024).toFixed(1)} KB
            </div>
          )}
        </div>
      </div>

      {/* Clash detection */}
      <div style={{ ...card, padding: 14, overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={label}>29.2 — Multi-Discipline Clash Detection</div>
          <button data-testid="bim-resolve-btn"
                   style={{ ...btn, background: "#16a34a" }}
                   onClick={async () => {
                      try {
                        const { data } = await api.post(`/projects/${projectId}/bim/clash-resolution`, {});
                        setResolution(data);
                      } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
                   }}>
            Run Resolution Workflow
          </button>
        </div>
        <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <Card l="Total" v={clashes?.total ?? "—"}
                color={clashes?.total ? "#ef4444" : "#22c55e"} />
          <Card l="Hard" v={clashes?.hard ?? 0} color="#ef4444" />
          <Card l="Soft" v={clashes?.soft ?? 0} color="#f59e0b" />
        </div>
        {clashes?.by_pair && (
          <div style={{ marginTop: 12 }}>
            <div style={{ ...label, fontSize: 9 }}>By discipline pair</div>
            {Object.entries(clashes.by_pair).map(([k, v]) => (
              <div key={k} style={{ fontSize: 11 }}>{k}: <b>{v}</b></div>
            ))}
          </div>
        )}
        {resolution?.summary && (
          <div data-testid="bim-resolution-result" style={{ marginTop: 12, padding: 10, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.4)", borderRadius: 6 }}>
            <div style={{ ...label, marginBottom: 6 }}>Resolution Workflow — {resolution.summary.total} clashes</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 6 }}>
              {Object.entries(resolution.summary.by_priority || {}).map(([p, n]) => (
                <Card key={p} l={p} v={n} color={
                  p === "CRITICAL" ? "#dc2626" : p === "HIGH" ? "#ef4444"
                  : p === "MEDIUM" ? "#f59e0b" : "#22c55e"
                } />
              ))}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {Object.entries(resolution.summary.by_discipline || {}).map(([k, v]) => (
                <span key={k} style={{ color: "var(--pf-muted)" }}>
                  {k}: <strong style={{ color: "#F26B1F" }}>{v}</strong>
                </span>
              ))}
            </div>
            <div style={{ maxHeight: 240, overflow: "auto", marginTop: 8 }}>
              <table style={{ width: "100%", fontSize: 10, borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "var(--pf-surface-2)", position: "sticky", top: 0 }}>
                  {["Clash", "Priority", "Discipline", "Suggested action", "Status"].map(h =>
                    <th key={h} style={{ padding: 4, textAlign: "left", color: "var(--pf-muted)" }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {(resolution.clashes || []).slice(0, 30).map((c) => (
                    <tr key={c.clash_id} data-testid={`bim-resolution-row-${c.clash_id}`} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                      <td style={{ padding: 4 }}>
                        <strong style={{ color: "#F26B1F" }}>{c.clash_id}</strong>
                      </td>
                      <td style={{ padding: 4, color:
                          c.priority === "CRITICAL" ? "#dc2626"
                        : c.priority === "HIGH" ? "#ef4444"
                        : c.priority === "MEDIUM" ? "#f59e0b" : "#22c55e",
                        fontWeight: 700,
                      }}>{c.priority}</td>
                      <td style={{ padding: 4 }}>{c.responsible_discipline}</td>
                      <td style={{ padding: 4 }}>{resolution.workflow.resolution_actions[c.suggested_action] || c.suggested_action}</td>
                      <td style={{ padding: 4 }}>{c.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 8, fontSize: 10, color: "var(--pf-muted)" }}>
              SLA: CRITICAL 24h · HIGH 3d · MEDIUM 1w · LOW next milestone
            </div>
          </div>
        )}
        <table style={{ width: "100%", marginTop: 16, fontSize: 10 }}>
          <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
            {["Element 1", "Element 2", "Type", "Level", "Owner", "Status"].map(h =>
              <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(clashes?.clashes || []).slice(0, 50).map(c => (
              <tr key={c.id} data-testid={`bim-clash-${c.id}`}>
                <td style={{ padding: 3 }}>{c.element_1}</td>
                <td style={{ padding: 3 }}>{c.element_2}</td>
                <td style={{ padding: 3, color: c.type === "Hard" ? "#ef4444" : "#f59e0b" }}>
                  {c.type}
                </td>
                <td style={{ padding: 3 }}>{c.level}</td>
                <td style={{ padding: 3 }}>{c.resolution_owner}</td>
                <td style={{ padding: 3 }}>{c.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {err && <div style={{ position: "absolute", bottom: 8, color: "#FF8888" }}>{err}</div>}
    </div>
  );
}

const DiscBlock = ({ title, data }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 10, color: "#F26B1F", fontWeight: 600 }}>{title}</div>
    {data && Object.entries(data).map(([k, v]) => (
      <div key={k} style={{ fontSize: 10, color: "var(--pf-muted)" }}>
        {k.replace(/_/g, " ")}: <b style={{ color: "var(--pf-text)" }}>{v ?? 0}</b>
      </div>
    ))}
  </div>
);
const Card = ({ l, v, color }) => (
  <div style={{ background: "var(--pf-bg)", border: "1px solid var(--pf-border)",
                  borderRadius: 4, padding: 8, textAlign: "center" }}>
    <div style={{ fontSize: 9, color: "var(--pf-muted)", textTransform: "uppercase" }}>{l}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: color || "var(--pf-text)", marginTop: 2 }}>{v}</div>
  </div>
);
